/**
 * cache/cycles.ts — the cycle library.
 *
 * Unexplored composes dungeons from **major cycle types**, each saying how the loop's
 * two arcs are used, and each producing a different narrative ebb and flow. It ships
 * 24 of them, on top of roughly 5000 hand-authored rewrite rules and a bespoke
 * authoring tool. We take the STRUCTURE and not the corpus: eight cycles here, one
 * level of nesting, and variety bought from theme × space embedding instead of rule
 * count (docs/design/caches-dungeon-research.md §4.1).
 *
 * SHAPE. Every cycle builds a fully traversable ungated loop with the goal hanging off
 * it, which is what makes lock-and-key sound — see the diagram in mission.ts. Arc
 * lengths scale with tier: a tier-0 run is one cycle, ~6–9 rooms, 3–5 minutes; a
 * tier-1 run has long arcs plus bypasses, ~15–25 rooms.
 */
import type { Rng } from "../rng.js";
import { pick, rangeInt } from "../rng.js";
import { MissionBuilder, nestSome, type MissionGraph } from "./mission.js";

export interface CycleDef {
  id: string;
  /** Tiers this cycle may be drawn for. */
  tiers: readonly number[];
  /** Selection weight within a tier. */
  weight: number;
  build(b: MissionBuilder): MissionGraph;
}

/** Nodes per arc, by tier. Tier 0 stays deliberately small — small levels cohere. */
function arcLen(b: MissionBuilder): number {
  return b.tier === 0 ? 2 : rangeInt(b.rng, 5, 7);
}

/** Bypasses to grow at this tier. Tier 0 gets none: one cycle, no nesting. */
function nestCount(b: MissionBuilder, max = 3): number {
  return b.tier === 0 ? 0 : max;
}

/**
 * An arc of `n` nodes, one of which is `role` at a random position along it.
 *
 * The special node is created IN PLACE. An earlier version built a full transit run and
 * then overwrote one slot, which left the displaced transit node alive in the graph with
 * no ring seat and no edges — a room the player could never reach. MissionBuilder.finish
 * now rejects that shape outright, but building it correctly is the actual fix.
 */
function arcWith(b: MissionBuilder, n: number, role: "key" | "vista" | "treasure" | "arena"): { arc: number[]; special: number } {
  const at = rangeInt(b.rng, 0, n - 1);
  const arc: number[] = [];
  let special = -1;
  for (let k = 0; k < n; k++) {
    if (k === at) { special = b.node(role, { pressure: role === "arena" ? 2 : 0 }); arc.push(special); }
    else arc.push(b.node("transit"));
  }
  return { arc, special };
}

export const CYCLES: readonly CycleDef[] = [
  {
    // Two genuinely different ways round to the boss: one fights, one sightsees.
    id: "alt-routes",
    tiers: [0, 1],
    weight: 3,
    build(b) {
      const E = b.node("entrance");
      const a = arcWith(b, arcLen(b), "arena");
      const bArc = arcWith(b, arcLen(b), "vista");
      const J = b.node("arena", { pressure: 2 });
      b.closeLoop([E, ...a.arc, J, ...bArc.arc]);
      nestSome(b, [...a.arc, ...bArc.arc], nestCount(b));
      const B = b.node("boss", { anchor: J, pressure: 3 });
      b.link(J, B, "door");
      return b.finish("alt-routes", E, B);
    },
  },
  {
    // The canonical one: the goal is sealed, and the key is on the far arc.
    id: "lock-and-key",
    tiers: [0, 1],
    weight: 3,
    build(b) {
      const E = b.node("entrance");
      const a = arcWith(b, arcLen(b), "key");
      const bArc = arcWith(b, arcLen(b), "arena");
      const J = b.node("transit", { pressure: 1 });
      b.closeLoop([E, ...a.arc, J, ...bArc.arc]);
      nestSome(b, [...a.arc, ...bArc.arc], nestCount(b));
      const G = b.node("gate", { anchor: J, pressure: 1 });
      const B = b.node("boss", { anchor: G, pressure: 3 });
      b.link(J, G, "gate", a.special); // ← the lock/key reference edge
      b.link(G, B, "door");
      return b.finish("lock-and-key", E, B);
    },
  },
  {
    // Two seals in series, one key per arc — you must walk the whole loop.
    id: "double-lock",
    tiers: [1],
    weight: 2,
    build(b) {
      const E = b.node("entrance");
      const a = arcWith(b, arcLen(b), "key");
      const bArc = arcWith(b, arcLen(b), "key");
      const J = b.node("arena", { pressure: 2 });
      b.closeLoop([E, ...a.arc, J, ...bArc.arc]);
      nestSome(b, [...a.arc, ...bArc.arc], nestCount(b, 2));
      const G1 = b.node("gate", { anchor: J, pressure: 1 });
      const G2 = b.node("gate", { anchor: G1, pressure: 1 });
      const B = b.node("boss", { anchor: G2, pressure: 3 });
      b.link(J, G1, "gate", a.special);
      b.link(G1, G2, "gate", bArc.special);
      b.link(G2, B, "door");
      return b.finish("double-lock", E, B);
    },
  },
  {
    // A hub with spokes off the ring: two hold keys, one holds loot for its own sake.
    id: "hub-spokes",
    tiers: [1],
    weight: 2,
    build(b) {
      const E = b.node("entrance");
      const arcA = b.transitRun(arcLen(b));
      const arcB = b.transitRun(arcLen(b));
      const J = b.node("arena", { pressure: 2 });
      b.closeLoop([E, ...arcA, J, ...arcB]);
      const spokeHosts = [pick(b.rng, arcA), pick(b.rng, arcB), J];
      const k1 = b.node("key", { anchor: spokeHosts[0] });
      const k2 = b.node("key", { anchor: spokeHosts[1] });
      const t = b.node("treasure", { anchor: spokeHosts[2], pressure: 1 });
      b.link(spokeHosts[0], k1, "tunnel");
      b.link(spokeHosts[1], k2, "tunnel");
      b.link(spokeHosts[2], t, "tunnel");
      nestSome(b, [...arcA, ...arcB], nestCount(b, 1));
      const G1 = b.node("gate", { anchor: J, pressure: 1 });
      const G2 = b.node("gate", { anchor: G1, pressure: 1 });
      const B = b.node("boss", { anchor: G2, pressure: 3 });
      b.link(J, G1, "gate", k1);
      b.link(G1, G2, "gate", k2);
      b.link(G2, B, "door");
      return b.finish("hub-spokes", E, B);
    },
  },
  {
    // A long way out and a one-way drop home — the loop only closes behind you.
    id: "shortcut-loop",
    tiers: [0, 1],
    weight: 2,
    build(b) {
      const E = b.node("entrance");
      const a = arcWith(b, arcLen(b) + 1, "treasure");
      const J = b.node("arena", { pressure: 2 });
      const S = b.node("shortcut", { pressure: 0 });
      b.closeLoop([E, ...a.arc, J, S], "door", "drop"); // S → E is one-way
      nestSome(b, a.arc, nestCount(b));
      const B = b.node("boss", { anchor: J, pressure: 3 });
      b.link(J, B, "door");
      return b.finish("shortcut-loop", E, B);
    },
  },
  {
    // Grab the prize at the far side, then fight your way back: pressure loads onto
    // the return arc, and the boss waits near the entrance you came in by.
    //
    // The vault door is GATED ON THE TREASURE ITSELF. Without that gate the cycle is
    // fully bypassable: closeLoop's wrap edge makes the last gauntlet room adjacent to
    // the entrance, so E → arcB[last] → boss is three rooms and the prize, the gauntlet
    // and every bypass are skipped. Locking the stub on J forces the intended route —
    // out along an arc to the prize, then back down the gauntlet under pressure.
    // (`lock` is a room index; the solver only checks that room is reachable first, so
    // a treasure node is a legal key.)
    id: "gauntlet-return",
    tiers: [0, 1],
    weight: 2,
    build(b) {
      const E = b.node("entrance");
      const arcA = b.transitRun(arcLen(b));
      const J = b.node("treasure", { pressure: 1 });
      const arcB = b.transitRun(arcLen(b));
      for (const i of arcB) b.nodes[i].pressure = 3; // the gauntlet
      b.closeLoop([E, ...arcA, J, ...arcB]);
      nestSome(b, arcA, nestCount(b, 2));
      const B = b.node("boss", { anchor: arcB[arcB.length - 1], pressure: 3 });
      b.link(arcB[arcB.length - 1], B, "gate", J); // ← the vault opens only once you hold the prize
      return b.finish("gauntlet-return", E, B);
    },
  },
  {
    // You SEE the prize early, across the loop, and cannot touch it until you have
    // walked the other arc. No edge between the vista and the treasure — that is the
    // whole point; the space layer places them facing each other.
    id: "vista-tease",
    tiers: [0, 1],
    weight: 2,
    build(b) {
      const E = b.node("entrance");
      const a = arcWith(b, arcLen(b), "vista");
      const bArc = arcWith(b, arcLen(b), "treasure");
      const J = b.node("transit", { pressure: 1 });
      b.closeLoop([E, ...a.arc, J, ...bArc.arc]);
      nestSome(b, [...a.arc, ...bArc.arc], nestCount(b));
      const B = b.node("boss", { anchor: J, pressure: 3 });
      b.link(J, B, "door");
      return b.finish("vista-tease", E, B);
    },
  },
  {
    // The boss owns the hub and both arcs are approach: prep, resources, position.
    // No stub at all — the goal is ON the loop, which is the one case where that is
    // sound, because there is nothing to lock.
    id: "guarded-hub",
    tiers: [0, 1],
    weight: 2,
    build(b) {
      const E = b.node("entrance");
      const a = arcWith(b, arcLen(b), "treasure");
      const bArc = arcWith(b, arcLen(b), "arena");
      const J = b.node("boss", { pressure: 3 });
      b.closeLoop([E, ...a.arc, J, ...bArc.arc]);
      nestSome(b, [...a.arc, ...bArc.arc], nestCount(b));
      return b.finish("guarded-hub", E, J);
    },
  },
];

/** Weighted pick among the cycles legal at `tier`. */
export function chooseCycle(rng: Rng, tier: number): CycleDef {
  const legal = CYCLES.filter((c) => c.tiers.includes(tier));
  let total = 0;
  for (const c of legal) total += c.weight;
  let roll = rng() * total;
  for (const c of legal) {
    roll -= c.weight;
    if (roll < 0) return c;
  }
  return legal[legal.length - 1];
}

/** Build the mission graph for one cycle. */
export function buildMission(def: CycleDef, rng: Rng, tier: number): MissionGraph {
  return def.build(new MissionBuilder(rng, tier));
}
